/**
 * Matter Select Component
 * Reusable dropdown for selecting Echo matters with billing rate display
 */

import React from 'react';
import { useEchoMatters, useMyMatters } from '../../api/echo';
import type { EchoMatter } from '../../api/types/echo';
import { Select, Stack, useTheme, textStyles, fontSize } from '@fti/design-system';

export interface MatterSelectProps {
  value: number | null;
  onChange: (matterId: number | null, matter: EchoMatter | null) => void;
  disabled?: boolean;
  required?: boolean;
  label?: string;
  placeholder?: string;
  showBillingRate?: boolean;
  /** When true (default), fetches only matters assigned to the current user.
   *  When false, fetches all matters (for admin pages like MatterManager). */
  userFiltered?: boolean;
}

/**
 * Dropdown select for Echo matters
 * Fetches available matters and displays them with optional billing rate info
 */
export function MatterSelect({
  value,
  onChange,
  disabled = false,
  required = false,
  label = 'Matter Code',
  placeholder = 'Select a matter...',
  showBillingRate = true,
  userFiltered = true,
}: MatterSelectProps) {
  const allMatters = useEchoMatters();
  const myMatters = useMyMatters();
  const { data: matters, loading, error } = userFiltered ? myMatters : allMatters;
  const { theme } = useTheme();

  const handleChange = (value: string) => {
    if (!value || value === '') {
      onChange(null, null);
      return;
    }
    const numValue = parseInt(value, 10);
    const selectedMatter = matters?.find((m) => m.id === numValue) ?? null;
    onChange(numValue, selectedMatter);
  };

  // Build options from matters data
  const options = React.useMemo(() => {
    if (!matters) return [];
    return matters.map((matter) => ({
      value: String(matter.id),
      label: showBillingRate
        ? `${matter.mattercode} - ${matter.name} ($${matter.billing_rate ?? 0}/min)`
        : `${matter.mattercode} - ${matter.name}`,
    }));
  }, [matters, showBillingRate]);

  // Find currently selected matter for display
  const selectedMatter = React.useMemo(() => {
    if (value === null || !matters) return null;
    return matters.find((m) => m.id === value) ?? null;
  }, [value, matters]);

  if (error) {
    return (
      <Stack gap="xs">
        {label && (
          <label
            style={{
              fontSize: fontSize.sm,
              fontWeight: 500,
              color: theme.colors.text.primary,
            }}
          >
            {label}
            {required && (
              <span style={{ color: theme.colors.semantic.error.default }}> *</span>
            )}
          </label>
        )}
        <div
          style={{
            padding: '8px 12px',
            background: `${theme.colors.semantic.error.light}20`,
            border: `1px solid ${theme.colors.semantic.error.light}`,
            borderRadius: '6px',
            color: theme.colors.semantic.error.default,
            fontSize: fontSize.sm,
          }}
        >
          Failed to load matters: {error.message}
        </div>
      </Stack>
    );
  }

  // Empty state when user has no assigned matters
  if (userFiltered && !loading && matters && matters.length === 0) {
    return (
      <Stack gap="xs">
        {label && (
          <label
            style={{
              ...textStyles.label,
              color: theme.colors.text.primary,
            }}
          >
            {label}
            {required && (
              <span style={{ color: theme.colors.semantic.error.default }}> *</span>
            )}
          </label>
        )}
        <div
          style={{
            padding: '8px 12px',
            background: `${theme.colors.semantic.warning.light}20`,
            border: `1px solid ${theme.colors.semantic.warning.light}`,
            borderRadius: '6px',
            color: theme.colors.semantic.warning.default,
            fontSize: fontSize.sm,
          }}
        >
          No matters assigned. Contact an administrator.
        </div>
      </Stack>
    );
  }

  return (
    <Stack gap="xs">
      {label && (
        <label
          style={{
            ...textStyles.label,
            color: theme.colors.text.primary,
          }}
        >
          {label}
          {required && (
            <span style={{ color: theme.colors.semantic.error.default }}> *</span>
          )}
        </label>
      )}
      <Select
        value={value !== null ? String(value) : ''}
        onChange={handleChange}
        disabled={disabled || loading}
        placeholder={loading ? 'Loading matters...' : placeholder}
        options={[
          { value: '', label: placeholder },
          ...options,
        ]}
      />
      {selectedMatter && showBillingRate && (
        <div
          style={{
            fontSize: fontSize.xs,
            color: theme.colors.text.secondary,
          }}
        >
          Billing rate: ${selectedMatter.billing_rate ?? 0}/min
        </div>
      )}
    </Stack>
  );
}

export default MatterSelect;
